import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { mostrarAlertaError } from '@/helpers/alert';
import api from '@/providers/api';
import { ACTIVO, CARGANDO, INACTIVO } from '../constantes';
import { parametroSimAdapter } from '@/models/adapters/parametroSim.adapter';
import {
  PARAMETROS_SIMULACIONES,
} from '@/helpers/urls';
import { DatoSeleccion } from '@/models/componentes/select.model';
import { crearComboSeleccion } from '@/components/moleculas/Seleccion/combo';
import DteCrearOperacion from '@/models/store/dteOperacion.model';
import {
  CuentaGiro,
  TipoCuenta,
} from '@/components/paginas/Cliente/Operacion/Resumen/models/cuentaGiro.model';
import RespuestaApi from '@/models/respuestas/resApi.model';
import { ParametroSimulacion } from '@/models/respuestas/parametroSim.model';
import { obtenerInfoCuentasBancarias } from './mantenedorCuentasSlice';

type InfoCuentas = {
  tiposCuenta: TipoCuenta[];
  bancos: DatoSeleccion[];
};

export interface PayloadArchivos {
  nombre?: string;
  tipoArchivo: 'pdf' | 'xml';
  folio: number;
}

export type ObjetoCalculosDte = {
  montoDiferenciaPrecio: number;
  montoNoFinanciado: number;
  montoComision: number;
  montoAnticipo: number;
};

export type CertificadoStore = {
  url: string;
  nombreArchivo: string;
  contraseña: string;
};

export type PayloadCalculos = Record<number, ObjetoCalculosDte>;

export interface PayloadAgregarArchivos extends PayloadArchivos {
  url: string;
  mime: string;
}

export const obtenerInfoResumen = createAsyncThunk(
  'infoResumen/obtener',
  async () => {
    try {
      // Obtencion de la informacion necesaria para crear una operacion

      const {
        data: { resultado },
      } = await api.get<
        RespuestaApi<Record<string, number | ParametroSimulacion[]>>
      >(PARAMETROS_SIMULACIONES());

      const { cantidadOperacionesGiradas, parametros } = resultado;

      const { comboBancos, cuentasUsuario, tiposCuenta } =
        await obtenerInfoCuentasBancarias();

      return {
        cantidadOperacionesGiradas,
        parametros,
        cuentasUsuario,
        tiposCuenta,
        comboBancos,
      };
    } catch {
      mostrarAlertaError('Hubo un error al cargar los parametros');
    }
  }
);

export const OP = {
  SELECCION: 1,
  RESUMEN: 2,
};

export const estadoInicialOperacion = {
  // Paso actual
  paso: 1,

  // Dtes seleccionados en el listado
  seleccion: [] as DteCrearOperacion[],

  // seleccion: [
  //   {
  //     idTipoDocumento: 1,
  //     fechaVencimiento: '04-06-2024',
  //     diasPlazo: 60,
  //     deudorExisteFintec: true,
  //     tipoDoc: 33,
  //     noTipoDoc: 'Factura Electrónica',
  //     nro: '307',
  //     rutReceptor: '60511023-1',
  //     noEmpresa: 'DELEGACION PRESIDENCIAL PROVINCIAL EL LO',
  //     folio: 171264,
  //     feDocumento: '03-04-2024',
  //     totalReparos: '0',
  //     mtNeto: 3518000,
  //     mtExento: 0,
  //     mtIva: 668420,
  //     mtTotal: 1186420,
  //     feRecepcion: '03-04-2024',
  //     evento: '',
  //     codigoOtroImpto: '',
  //     valorOtroImpto: '',
  //     tasaOtroImpto: '',
  //     cedible: 'pending',
  //     xml: null,
  //     pdf: null,
  //   },
  //   {
  //     idTipoDocumento: 1,
  //     fechaVencimiento: '08-05-2024',
  //     diasPlazo: 33,
  //     deudorExisteFintec: true,
  //     tipoDoc: 33,
  //     noTipoDoc: 'Factura Electrónica',
  //     nro: '46',
  //     rutReceptor: '79528420-6',
  //     noEmpresa: 'INGENIERIA AGROSONDA SPA',
  //     folio: 171251,
  //     feDocumento: '01-04-2024',
  //     totalReparos: '0',
  //     mtNeto: 16500000,
  //     mtExento: 0,
  //     mtIva: 3135000,
  //     mtTotal: 1335000,
  //     feRecepcion: '01-04-2024',
  //     evento: '',
  //     codigoOtroImpto: '',
  //     valorOtroImpto: '',
  //     tasaOtroImpto: '',
  //     cedible: 'pending',
  //     xml: null,
  //     pdf: null,
  //   },
  // ] as DteCrearOperacion[],

  // Parametros para el resumen
  parametrosSim: [] as ParametroSimulacion[],

  // Manejo de la tabla con las cuentas de giro
  cuentasGiro: [] as CuentaGiro[],
  cuentaSeleccionada: {} as CuentaGiro,

  // Informacion requerida para la creacion de cuentas
  infoCuentas: {} as InfoCuentas,

  // Aceptacion de tyc asociados a la operacion
  tycAceptados: false,

  // Certificado guardado o cargado
  certificado: null as CertificadoStore,

  // Estados de carga
  estado: INACTIVO,

  cantidadOperacionesGiradas: 0,
};

const operacionSlice = createSlice({
  name: 'operacion',
  initialState: estadoInicialOperacion,
  reducers: {
    añadirDocumento: ({ seleccion }, action) => {
      seleccion.push({
        ...action.payload,
        cedible: 'pending',
        xml: null,
        pdf: null,
        montoAnticipo: 0,
        montoComision: 0,
        montoDiferenciaPrecio: 0,
        montoNoFinanciado: 0,
      });
    },
    actualizarEstadoDocumento: ({ seleccion }, action) => {
      const { folio, cedible } = action.payload;
      const dteEncontrado = seleccion.find((dte) => dte.folio === folio);
      if (dteEncontrado) dteEncontrado.cedible = cedible;
    },
    actualizarEstadosDocumentos: (state, action) => {
      // Actualizar estado de varios documentos a la vez
      // Utilizado para documentos encontrados en operaciones
      const { folios, cedible } = action.payload;

      state.seleccion.forEach((d) => {
        if (folios.includes(d.folio)) {
          d.cedible = cedible;
        }
      });
    },
    actualizarPaso: (state, action) => {
      state.paso = action.payload;
    },
    eliminarSeleccion: (state, action) => {
      return {
        ...state,
        seleccion: state.seleccion.filter((d) => d.folio !== action.payload),
      };
    },
    modificarCuentaSeleccionada: (state, { payload }) => {
      const cuentaEncontrada = state.cuentasGiro.find(
        (cg) => cg.id === payload
      );
      if (cuentaEncontrada) {
        state.cuentaSeleccionada = cuentaEncontrada;
      } else {
        mostrarAlertaError('Ocurrio un error desconocido');
      }
    },
    agregarCuentaGiro: (state, action) => {
      const cuenta = action.payload as CuentaGiro;

      if (state.cuentasGiro.length === 0) {
        state.cuentaSeleccionada = cuenta;
        state.cuentasGiro.push(cuenta);
      } else {
        if (cuenta.predeterminada) {
          // Si es predeterminada la agrego al principio del array
          state.cuentasGiro.unshift(cuenta);
          state.cuentaSeleccionada = cuenta;
        } else {
          // Sino la pusheo al final
          state.cuentasGiro.push(cuenta);
        }
      }
    },
    eliminarCuentaGiro: (state, { payload }) => {
      state.cuentasGiro = state.cuentasGiro.filter((c) => c.id !== payload);

      if (state.cuentaSeleccionada.id === payload) {
        // Establezco la primera de la lista en caso de que haya cuentas restantes
        if (state.cuentasGiro.length > 0) {
          state.cuentaSeleccionada = state.cuentasGiro[0];
        } else {
          // Sino reestablezco el estado
          state.cuentaSeleccionada = {} as CuentaGiro;
        }
      }
    },
    agregarArchivo: (state, { payload }) => {
      const { folio, tipoArchivo, url, nombre, mime } =
        payload as PayloadAgregarArchivos;
      state.seleccion.forEach((s) => {
        if (s.folio === folio) {
          s[tipoArchivo] = {
            url,
            nombre,
            mime,
          };
        }
      });
    },
    agregarCertificado: (
      state,
      { payload }: PayloadAction<CertificadoStore>
    ) => {
      state.certificado = payload;
    },
    eliminarArchivo: (state, { payload }: PayloadAction<PayloadArchivos>) => {
      const { folio, tipoArchivo } = payload;
      state.seleccion.forEach((s) => {
        if (s.folio === folio) {
          s[tipoArchivo] = null;
        }
      });
    },
    aceptarTerminos: (state) => {
      state.tycAceptados = !state.tycAceptados;
    },
    agregarDescuentosDocumentos: (
      state,
      { payload }: PayloadAction<PayloadCalculos>
    ) => {
      const datos = payload;
      state.seleccion = state.seleccion.map((s) => ({
        ...s,
        ...datos[s.folio],
      }));
    },
    reiniciarOperacion: () => {
      return { ...estadoInicialOperacion };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(obtenerInfoResumen.pending, () => {
        // Reinicio el estado para evitar que queden datos sin uso desde una sesion anterior
        return {
          ...estadoInicialOperacion,
          estado: CARGANDO,
        };
      })
      .addCase(
        obtenerInfoResumen.fulfilled,
        (
          state,
          {
            payload: {
              parametros,
              cantidadOperacionesGiradas,
              cuentasUsuario,
              tiposCuenta,
              comboBancos,
            },
          }
        ) => {
          state.estado = ACTIVO;
          state.parametrosSim = parametroSimAdapter(
            parametros as ParametroSimulacion[]
          );
          state.cantidadOperacionesGiradas =
            cantidadOperacionesGiradas as number;
          state.cuentasGiro = cuentasUsuario;

          // Establezco la cuenta predeterminada como la seleccion inicial
          state.cuentaSeleccionada =
            cuentasUsuario.find((cg) => cg.predeterminada) || cuentasUsuario[0];
          state.infoCuentas = {
            tiposCuenta,
            bancos: crearComboSeleccion(comboBancos, 'nombre', 'id'),
          };
        }
      );
  },
});

export const {
  añadirDocumento,
  actualizarEstadoDocumento,
  eliminarSeleccion,
  actualizarPaso,
  actualizarEstadosDocumentos,
  agregarCuentaGiro,
  eliminarCuentaGiro,
  modificarCuentaSeleccionada,
  agregarArchivo,
  eliminarArchivo,
  aceptarTerminos,
  agregarDescuentosDocumentos,
  agregarCertificado,
  reiniciarOperacion,
} = operacionSlice.actions;

export default operacionSlice.reducer;
